// ComposerListbox — the shared `role="listbox"` popup body for PowerComposer's
// two combobox triggers (slash-commands, @-mentions), pure.
//
// Mirrors the WAI-ARIA `aria-activedescendant` combobox pattern already
// established in this codebase (CommandPalette.tsx, Editor/SlashMenu/
// SlashMenu.tsx, Finder.tsx): the TEXTAREA keeps DOM focus (see
// PowerComposer.tsx's header for why the textarea itself stays role-less
// rather than an explicit `role="combobox"` — axe rejects that role on a
// multiline element), this popup is `role="listbox"` (id = the textarea's
// `aria-controls`), rows are `role="option"` and are NOT focus targets —
// the textarea points at the active one via `aria-activedescendant`.
// Generic over the item type so the
// slash-command menu and the mention menu — deliberately DIFFERENT row
// content per research doc §3 ("distinct trigger characters and menu
// styling") — share one accessible listbox implementation instead of two
// near-duplicate ones.
//
// Pure/props-driven: no state of its own; the host (PowerComposer) owns the
// query, the active index, and commits a pick back up.

import { type ReactNode, useEffect, useRef } from "react";
import "./ComposerListbox.css";

export type ComposerListboxProps<Item> = {
    readonly listboxId: string;
    readonly ariaLabel: string;
    readonly items: readonly Item[];
    readonly activeIndex: number;
    readonly emptyLabel: ReactNode;
    readonly optionId: (index: number) => string;
    readonly keyOf: (item: Item) => string;
    readonly renderOption: (item: Item, active: boolean) => ReactNode;
    // A row was chosen (click). Keyboard Enter is the host's job — it
    // already knows the active index.
    readonly onCommit: (item: Item) => void;
    // Pointer moved onto a row — the host syncs its keyboard cursor to it so
    // mouse and keyboard share one highlight.
    readonly onHoverIndex: (index: number) => void;
};

export const ComposerListbox = <Item,>({
    listboxId,
    ariaLabel,
    items,
    activeIndex,
    emptyLabel,
    optionId,
    keyOf,
    renderOption,
    onCommit,
    onHoverIndex,
}: ComposerListboxProps<Item>): ReactNode => {
    const listRef = useRef<HTMLDivElement>(null);
    // Keep the keyboard-active row visible (view-local DOM effect).
    // items.length re-runs it when the list changes under a fixed index.
    // biome-ignore lint/correctness/useExhaustiveDependencies: see above
    useEffect(() => {
        // Option rows are the listbox's direct children, in item order.
        listRef.current?.children
            .item(activeIndex)
            ?.scrollIntoView({ block: "nearest" });
    }, [activeIndex, items.length]);
    return (
        <div
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-label={ariaLabel}
            className="bilda-composer-listbox"
        >
            {items.length === 0 ? (
                // A `role="listbox"` requires an `option`/`group` child (axe's
                // aria-required-children) even when the result set is empty —
                // `role="presentation"` here would strip the listbox of its
                // only child and fail that check. A disabled, non-committable
                // option row is the ARIA-legal way to say "nothing matched".
                <div
                    className="bilda-composer-listbox__empty"
                    role="option"
                    tabIndex={-1}
                    aria-disabled="true"
                    aria-selected="false"
                >
                    {emptyLabel}
                </div>
            ) : (
                items.map((item, index) => (
                    <div
                        key={keyOf(item)}
                        id={optionId(index)}
                        role="option"
                        // Programmatically addressable but not a tab stop — the
                        // textarea keeps DOM focus and points here via
                        // aria-activedescendant (the CommandPalette/SlashMenu
                        // precedent).
                        tabIndex={-1}
                        aria-selected={index === activeIndex}
                        className="bilda-composer-listbox__option"
                        // Commit on mousedown, not click: the textarea must not
                        // lose focus (and close the menu) before the pick
                        // registers.
                        onMouseDown={(event) => {
                            event.preventDefault();
                            onCommit(item);
                        }}
                        onMouseEnter={() => onHoverIndex(index)}
                    >
                        {renderOption(item, index === activeIndex)}
                    </div>
                ))
            )}
        </div>
    );
};
